/**
 * ═══════════════════════════════════════════════════════════════════════════
 * G-38 — "am I running inside the tablet app?"
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * The shop tablet runs a thin Capacitor shell whose WebView loads the LIVE
 * kiosk page from flowerworld.cc (FINALIZING.md §6 G-38, ruling OD-33). The
 * same page is opened every day in an ordinary desktop browser, so this file
 * answers one question and nothing more: which of the two is this?
 *
 * Deliberately reads `window.Capacitor` rather than importing `@capacitor/core`.
 * The bridge is injected by the native runtime into the page it loads, so
 * importing the package would ship a dependency to every browser visitor to
 * learn something the global already says. Nothing here throws or touches the
 * network, so it is safe to call during render.
 *
 * The rule this exists to serve (G-27's interaction rule 5, and the owner's
 * 2026-08-04 constraint): a shell-only capability is ABSENT in a browser, never
 * broken, and a browser-only affordance is absent in the shell — never a dead
 * control. When the NFC and Bluetooth capabilities arrive they extend this
 * module rather than sniffing the user agent.
 */

interface CapacitorGlobal {
  isNativePlatform?: () => boolean;
  getPlatform?: () => string;
}

function capacitor(): CapacitorGlobal | undefined {
  if (typeof window === "undefined") return undefined;
  return (window as Window & { Capacitor?: CapacitorGlobal }).Capacitor;
}

/**
 * True only inside the native tablet app. False in every browser, including
 * the same page opened on the tablet's own Chrome — which is correct: without
 * the shell there is no NFC reader and no printer to reach.
 */
export function isNativeShell(): boolean {
  try {
    return capacitor()?.isNativePlatform?.() === true;
  } catch {
    // A hostile or half-initialised global must never break the kiosk.
    return false;
  }
}
